import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Input } from "./ui/input";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plug, Loader2 } from "lucide-react";

const eur = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));
const eur2 = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("it-IT", { style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const num = (v) => Number(v).toLocaleString("it-IT");

export default function EvBenefitEditor({ lead, onUpdate, canEdit = true, selfToken = null }) {
  const ev0 = lead?.cost_estimate?.ev_benefit || {};
  const [models, setModels] = useState([]);
  const [kmDefault, setKmDefault] = useState(15000);
  const [enabled, setEnabled] = useState(!!ev0.enabled);
  const [km, setKm] = useState(ev0.km_year != null ? String(ev0.km_year) : "");
  const [consumo, setConsumo] = useState(ev0.consumo_kwh_100 != null ? String(ev0.consumo_kwh_100) : "");
  const [modelSel, setModelSel] = useState(ev0.model_label || "");
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    api.get(selfToken ? "/public/ev-models" : "/ev-models").then((r) => { setModels(r.data.models || []); setKmDefault(r.data.km_default || 15000); }).catch(() => {});
    if (!selfToken && ev0.enabled && ev0.consumo_kwh_100) {
      api.get(`/leads/${lead.id}/outlook`).then((r) => setPreview(r.data.consulenza?.ev_benefit || r.data.piano?.ev_benefit || null)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id]);

  const save = async (over = {}) => {
    const payload = {
      enabled: over.enabled !== undefined ? over.enabled : enabled,
      km_year: over.km_year !== undefined ? over.km_year : (km === "" ? null : Number(km)),
      consumo_kwh_100: over.consumo_kwh_100 !== undefined ? over.consumo_kwh_100 : (consumo === "" ? null : Number(consumo)),
      model_label: over.model_label !== undefined ? over.model_label : (modelSel || null),
    };
    setSaving(true);
    try {
      const url = selfToken ? `/public/self/${selfToken}/ev-benefit` : `/leads/${lead.id}/ev-benefit`;
      const { data } = await api.post(url, payload);
      if (onUpdate && !selfToken) onUpdate(data);
      if (!selfToken && payload.enabled && payload.consumo_kwh_100) {
        try { const { data: ol } = await api.get(`/leads/${lead.id}/outlook`); setPreview(ol.consulenza?.ev_benefit || ol.piano?.ev_benefit || null); } catch { setPreview(null); }
      } else { setPreview(null); }
      toast.success("Copertura auto elettrica aggiornata");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="rounded border-emerald-500/30 bg-emerald-500/5 p-5" data-testid="ev-block">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Plug className="h-4 w-4 text-emerald-500" strokeWidth={1.5} />
          <span className="text-sm font-semibold">Copertura costo km auto elettrica</span>
          {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-emerald-500" />}
        </div>
        <Switch checked={enabled} onCheckedChange={(v) => { setEnabled(v); save({ enabled: v }); }} disabled={saving || !canEdit} data-testid="ev-switch" />
      </div>
      <p className="text-xs text-muted-foreground mt-3">Stima il risparmio annuo di ricarica coperto dal surplus fotovoltaico: <b>km/anno × consumo (kWh/100km) × prezzo energia</b>. Mostrato nella Consulenza (pre-Outlook) e nella proposta al cliente.</p>
      {enabled && (
        <div className="mt-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Modello auto</label>
              <Select value={modelSel} onValueChange={(v) => { const m = models.find((x) => `${x.brand} ${x.model}` === v); setModelSel(v); if (m) setConsumo(String(m.kwh_100km)); save({ model_label: v, consumo_kwh_100: m ? m.kwh_100km : undefined, enabled: true }); }} disabled={!canEdit}>
                <SelectTrigger data-testid="ev-model-select"><SelectValue placeholder="Seleziona (o manuale)" /></SelectTrigger>
                <SelectContent>
                  {models.map((m) => { const lbl = `${m.brand} ${m.model}`; return <SelectItem key={lbl} value={lbl}>{lbl} · {m.kwh_100km} kWh/100km</SelectItem>; })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Consumo (kWh/100km)</label>
              <Input type="number" step="0.1" value={consumo} onChange={(e) => setConsumo(e.target.value)} onBlur={() => save()} placeholder="es. 15" disabled={!canEdit} data-testid="ev-consumo-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Km / anno</label>
              <Input type="number" value={km} onChange={(e) => setKm(e.target.value)} onBlur={() => save()} placeholder={String(kmDefault)} disabled={!canEdit} data-testid="ev-km-input" />
            </div>
          </div>
          {preview && (
            <div className="rounded border border-emerald-500/30 bg-background/40 p-3 grid grid-cols-2 gap-3" data-testid="ev-benefit-result">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Risparmio ricarica / anno</div>
                <div className="text-2xl font-heading font-bold text-emerald-500">{eur(preview.annuo)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">In {preview.anni} anni</div>
                <div className="text-2xl font-heading font-bold">{eur(preview.totale)}</div>
              </div>
              <p className="col-span-2 text-[11px] text-muted-foreground">Fabbisogno auto {num(preview.fabbisogno_kwh)} kWh/anno · coperti dal FV {num(preview.coperti_kwh)} kWh ({preview.copertura_pct}%) × {eur2(preview.prezzo_energia)}/kWh.</p>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}
